import React, { useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { FiInfo } from "react-icons/fi";
import { toast } from "react-toastify";
import { Button, CustomSelect, Select } from "../../components/ui";
import {
  aiContentGetStrategy,
  aiContentUpsertStrategy,
  aiContentGeneratePlan,
  type AiContentStrategy,
  type GenerationsInfo,
} from "../../utils/aiContent";
import { CAMPAIGN_TIMEZONE_OPTIONS } from "../CampaignManagement/campaignFlow";
import type { AiContentOutletContext } from "./AiContentLayout";
import { aiOutlineBtn, aiPrimaryBtn } from "./aiContentUi";

const buildHourlyOptions = () =>
  Array.from({ length: 24 }).map((_, hour) => {
    const label = `${String(hour).padStart(2, "0")}:00`;
    return { value: label, label };
  });

const HOUR_OPTIONS = buildHourlyOptions();

const FREQUENCY_OPTIONS = [
  { value: "daily", label: "Daily" },
  { value: "weekdays", label: "Weekdays only" },
  { value: "every_other_day", label: "Every other day" },
  { value: "weekly", label: "Weekly" },
];

const FREQUENCY_HELP_ITEMS = [
  {
    label: "Daily",
    text: "One post every day (default). Best for steady visibility.",
  },
  {
    label: "Weekdays only",
    text: "Posts Mon–Fri only. Skips Saturday and Sunday.",
  },
  {
    label: "Every other day",
    text: "Posts on alternating days (e.g. day 1, 3, 5…).",
  },
  {
    label: "Weekly",
    text: "About one post per week — lower volume, longer plan span.",
  },
];

const normalizeHourValue = (raw?: string) => {
  const value = String(raw || "09:00").trim();
  if (HOUR_OPTIONS.some((o) => o.value === value)) return value;
  const match = value.match(/^(\d{1,2})/);
  if (!match) return "09:00";
  const hour = Math.min(23, Math.max(0, parseInt(match[1], 10)));
  return `${String(hour).padStart(2, "0")}:00`;
};

const resolveTimezoneValue = (raw?: string) => {
  const value = String(raw || "").trim();
  if (CAMPAIGN_TIMEZONE_OPTIONS.some((o) => o.value === value)) return value;
  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  if (CAMPAIGN_TIMEZONE_OPTIONS.some((o) => o.value === browserTz)) return browserTz;
  return "Europe/London";
};

const emptyForm: AiContentStrategy = {
  objective: "",
  audience: "",
  topics: "",
  tone: "",
  endGoal: "",
  productsServices: "",
  avoidTopics: "",
  ctaPreference: "",
  postingFrequency: "daily",
  preferredPostingTime: "09:00",
  timezone: resolveTimezoneValue(),
  additionalInstructions: "",
};

const Field = ({
  label,
  children,
  hint,
  infoContent,
}: {
  label: string;
  children: React.ReactNode;
  hint?: string;
  infoContent?: React.ReactNode;
}) => (
  <div className="block">
    <div className="mb-1 flex items-center gap-1.5">
      <span className="text-sm font-semibold text-flow-config-text">{label}</span>
      {infoContent ? (
        <span className="group relative inline-flex">
          <button
            type="button"
            className="inline-flex h-4 w-4 items-center justify-center rounded-full text-muted hover:text-brand-blue"
            aria-label={`${label} help`}
          >
            <FiInfo className="h-3.5 w-3.5" aria-hidden />
          </button>
          <span
            role="tooltip"
            className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-2 hidden w-64 -translate-x-1/2 rounded-[8px] border border-line bg-white px-3 py-2.5 text-left text-xs font-normal text-flow-config-text shadow-[0_8px_24px_rgba(17,15,42,0.12)] group-hover:block group-focus-within:block"
          >
            {infoContent}
          </span>
        </span>
      ) : null}
    </div>
    {children}
    {hint ? <span className="mt-1 block text-xs text-muted">{hint}</span> : null}
  </div>
);

const inputClass =
  "w-full rounded-[8px] border border-line bg-white px-3 py-2.5 text-sm text-flow-config-text outline-none focus:border-brand-blue";

const StrategyPage = () => {
  const navigate = useNavigate();
  const { ownerId, userId, workspaceId } =
    useOutletContext<AiContentOutletContext>();
  const [form, setForm] = useState<AiContentStrategy>(emptyForm);
  const [generations, setGenerations] = useState<GenerationsInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  const scope = { ownerId, userId, workspaceId };

  const load = async () => {
    setLoading(true);
    try {
      const data = await aiContentGetStrategy(scope);
      if (data?.strategy) {
        setForm({
          ...emptyForm,
          ...data.strategy,
          postingFrequency: FREQUENCY_OPTIONS.some(
            (o) => o.value === data.strategy.postingFrequency
          )
            ? data.strategy.postingFrequency
            : "daily",
          preferredPostingTime: normalizeHourValue(
            data.strategy.preferredPostingTime
          ),
          timezone: resolveTimezoneValue(data.strategy.timezone),
        });
      } else {
        setForm(emptyForm);
      }
      if (data?.generations) setGenerations(data.generations);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Failed to load strategy");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (ownerId) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerId]);

  const setField = (key: keyof AiContentStrategy, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await aiContentUpsertStrategy(scope, form);
      toast.success("Strategy saved");
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Failed to save strategy");
    } finally {
      setSaving(false);
    }
  };

  const saveAndGenerate = async () => {
    setGenerating(true);
    try {
      await aiContentUpsertStrategy(scope, form);
      const data = await aiContentGeneratePlan(scope);
      toast.success("30-day plan generated — review and approve before publishing");
      if (data?.generations) setGenerations(data.generations);
      navigate("/ai-content/plan");
    } catch (e: any) {
      toast.error(
        e?.response?.data?.error || "Failed to generate 30-day plan"
      );
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return <p className="text-sm text-muted">Loading strategy…</p>;
  }

  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-[15px] font-semibold tracking-tight text-flow-config-text">
            Content Strategy
          </h2>
          <p className="text-sm text-muted">
            Brief for this LinkedIn account. Editable anytime.
          </p>
        </div>
        {generations ? (
          <span className="rounded-[8px] border border-line bg-bg-light-blue px-3 py-1.5 text-xs font-semibold text-brand-blue">
            Plan generations this month: {generations.used} of {generations.limit}{" "}
            used ({generations.remaining} left)
          </span>
        ) : null}
      </div>

      <div className="space-y-4 rounded-[10px] border border-line bg-white p-5">
        <Field label="Main objective / aim">
          <textarea
            className={inputClass}
            rows={2}
            value={form.objective || ""}
            onChange={(e) => setField("objective", e.target.value)}
            placeholder="Position me as an expert in AI automation"
          />
        </Field>
        <Field label="Target audience">
          <textarea
            className={inputClass}
            rows={2}
            value={form.audience || ""}
            onChange={(e) => setField("audience", e.target.value)}
            placeholder="Saudi startup founders and CEOs"
          />
        </Field>
        <Field label="Topics / expertise">
          <textarea
            className={inputClass}
            rows={2}
            value={form.topics || ""}
            onChange={(e) => setField("topics", e.target.value)}
          />
        </Field>
        <Field label="Desired tone">
          <input
            className={inputClass}
            value={form.tone || ""}
            onChange={(e) => setField("tone", e.target.value)}
            placeholder="Personal, knowledgeable, conversational"
          />
        </Field>
        <Field label="End goal">
          <textarea
            className={inputClass}
            rows={2}
            value={form.endGoal || ""}
            onChange={(e) => setField("endGoal", e.target.value)}
            placeholder="Generate inbound enquiries for software development"
          />
        </Field>
        <Field label="Products / services to promote indirectly">
          <textarea
            className={inputClass}
            rows={2}
            value={form.productsServices || ""}
            onChange={(e) => setField("productsServices", e.target.value)}
          />
        </Field>
        <Field label="Topics to avoid">
          <textarea
            className={inputClass}
            rows={2}
            value={form.avoidTopics || ""}
            onChange={(e) => setField("avoidTopics", e.target.value)}
            placeholder="Direct sales pitches"
          />
        </Field>
        <Field label="CTA preference">
          <input
            className={inputClass}
            value={form.ctaPreference || ""}
            onChange={(e) => setField("ctaPreference", e.target.value)}
          />
        </Field>
        <div className="grid gap-4 sm:grid-cols-3">
          <Field
            label="Posting frequency"
            infoContent={
              <ul className="space-y-2">
                {FREQUENCY_HELP_ITEMS.map((item) => (
                  <li key={item.label}>
                    <span className="font-semibold text-brand-blue">
                      {item.label}:
                    </span>{" "}
                    <span className="text-muted">{item.text}</span>
                  </li>
                ))}
              </ul>
            }
          >
            <Select
              options={FREQUENCY_OPTIONS}
              value={form.postingFrequency || "daily"}
              onChange={(value) => setField("postingFrequency", value)}
              size="sm"
              ariaLabel="Posting frequency"
              className="h-10 !border-line font-semibold text-dark-blue"
            />
          </Field>
          <Field label="Preferred posting time">
            <Select
              options={HOUR_OPTIONS}
              value={normalizeHourValue(form.preferredPostingTime)}
              onChange={(value) => setField("preferredPostingTime", value)}
              size="sm"
              ariaLabel="Preferred posting time"
              className="h-10 !border-line font-semibold text-dark-blue"
            />
          </Field>
          <Field label="Timezone">
            <CustomSelect
              options={CAMPAIGN_TIMEZONE_OPTIONS}
              value={
                CAMPAIGN_TIMEZONE_OPTIONS.find(
                  (option) => option.value === form.timezone
                ) || null
              }
              onChange={(option) =>
                setField("timezone", option?.value || "Europe/London")
              }
              placeholder="Select timezone"
              isBorder
              isSearchable
              controlBackgroundColor="#FFFFFF"
              placeholderColor="black"
              controlMinHeight={40}
              controlBorderRadius={8}
            />
          </Field>
        </div>
        <Field label="Additional instructions">
          <textarea
            className={inputClass}
            rows={3}
            value={form.additionalInstructions || ""}
            onChange={(e) => setField("additionalInstructions", e.target.value)}
          />
        </Field>

        <div className="flex flex-wrap gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            className={aiOutlineBtn}
            onClick={() => void save()}
            isLoading={saving}
            isDisabled={saving || generating}
          >
            Save strategy
          </Button>
          <Button
            variant="primary"
            size="sm"
            className={aiPrimaryBtn}
            onClick={() => void saveAndGenerate()}
            isLoading={generating}
            isDisabled={
              saving ||
              generating ||
              (generations != null && generations.remaining <= 0)
            }
          >
            Save & generate 30-day plan
          </Button>
        </div>
      </div>
    </div>
  );
};

export default StrategyPage;
